import { SearchUser } from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import toast from 'react-hot-toast';
import { stores } from 'state/stores';
import API from 'utils/API';
import { sendEvent } from 'utils/events';
import { UserListModalViewModalInterface } from './types';
import { t } from 'translations/t';

interface FollowingUserListViewModelProps {
  mode: 'following';
  username: string;
}

interface LikesUserListViewModelProps {
  mode: 'likes';
  workoutId: string;
}

type UserListViewModelProps = FollowingUserListViewModelProps | LikesUserListViewModelProps;

export class UserListViewModel implements UserListModalViewModalInterface {
  title: string;
  searchText: string = '';
  _noResultsText;
  isLoading: boolean = false;
  private _users: SearchUser[] = [];
  private fetch: () => Promise<SearchUser[]>;

  constructor(props: UserListViewModelProps) {
    makeAutoObservable(this);

    switch (props.mode) {
      case 'following':
        this.fetch = () => API.getFollowing(props.username).then(res => res.data);
        this.title = t('global.following');
        this._noResultsText = t('web.followersListModal.notFollowingAnyone', {
          username: props.username,
        });
        break;
      case 'likes':
        this.fetch = () => {
          if (props.workoutId) {
            return API.getWorkoutLikes(props.workoutId).then(res => res.data);
          } else {
            return Promise.resolve([]);
          }
        };
        this.title = t('global.likes');
        this._noResultsText = t('web.userListModal.noLikesYet');
        break;
    }
    this.fetchUsers();
  }

  get users() {
    return this._users.filter(u => {
      return (
        u.username.toLowerCase().includes(this.searchText.toLowerCase()) ||
        u.full_name?.toLowerCase().includes(this.searchText.toLowerCase())
      );
    });
  }

  get noResultsText() {
    if (this.searchText.length > 0) {
      return t('web.followersListModal.noResults', { searchText: this.searchText });
    } else {
      return this._noResultsText;
    }
  }

  handleScroll = async () => {
    return;
  };

  private fetchUsers = async () => {
    this.isLoading = true;
    this._users = await this.fetch();
    this.isLoading = false;
  };

  onSearchTextChange = (value: string) => {
    this.searchText = value;
  };

  onFollowUserClick = async (user: SearchUser) => {
    sendEvent('userList_userFollowPress', { userId: user.id });
    try {
      this._users.find(u => u.id === user.id)!.following_status = 'requested';
      await stores.followingStatus.follow(user.username);
    } catch (e) {
      toast.error(t('web.followersListModal.followFail'));
    } finally {
      this._users.find(u => u.id === user.id)!.following_status = stores.followingStatus.status(
        user.username,
      );
    }
  };

  onUnfollowUserClick = async (user: SearchUser) => {
    sendEvent('userList_userUnfollowPress', { userId: user.id });
    try {
      this._users.find(u => u.id === user.id)!.following_status = 'not-following';
      await stores.followingStatus.unfollow(user.username);
    } catch {
      toast.error(t('web.followersListModal.unfollowFail'));
    } finally {
      this._users.find(u => u.id === user.id)!.following_status = stores.followingStatus.status(
        user.username,
      );
    }
  };
}
